import { computed, observable } from "mobx";

import { ArrayUtils } from "../../utils/ArrayUtils";
import { Word } from "./Word";

const IS_DEBUGGING = false;

export interface ILoanWordsStore {
  allWords: Word[];
  getLanguages(filter: WordFilter): string[];
  getWordCount(): number;
  getWordCountForLanguage(language: string): number;
  getWords(filter: WordFilter): Word[];
  getWordsNotFilteredByWord(filter: WordFilter): Word[];
  setWords(words: Word[]);
  getRandomLanguageFromCountry(country: string): string | null;
  getLanguagesFromCountry(country: string): string[];
}

export class LoanWordsStore implements ILoanWordsStore {
  @observable private wordsValue: Word[] = [];

  setWords(words: Word[]) {
    this.wordsValue = words;
  }

  private getFilteredWords(filter: WordFilter): Word[] {
    const sortedWords = this.wordsValue.filter(
      word =>
        (!filter.originalLanguage ||
          word.originalLanguage === filter.originalLanguage) &&
        (!filter.originalWord || word.originalWord === filter.originalWord)
    );

    sortedWords.sort((a, b) => a.originalWord.localeCompare(b.originalWord));

    return sortedWords;
  }

  getLanguages(filter: WordFilter): string[] {
    const languages = this.getFilteredWords(filter).map(
      word => word.originalLanguage
    );

    const uniqueLanguages = languages.filter(
      (item, pos) => languages.indexOf(item) === pos
    );

    uniqueLanguages.sort((a, b) => a.localeCompare(b));

    return uniqueLanguages;
  }

  @computed
  get allWords(): Word[] {
    return this.wordsValue;
  }

  getWords(filter: WordFilter): Word[] {
    return this.getFilteredWords(filter);
  }

  getWordCount(): number {
    return this.wordsValue.length;
  }

  getWordCountForLanguage(language: string): number {
    return this.wordsValue.filter(word => word.originalLanguage === language)
      .length;
  }

  getWordsNotFilteredByWord(filter: WordFilter): Word[] {
    const newFilter = {
      originalLanguage: filter.originalLanguage
    };

    return this.getWords(newFilter);
  }

  getLanguagesFromCountry(country: string): string[] {
    const languages = ArrayUtils.getDistinctArray(
      this.allWords
        .filter(word => word.country === country)
        .map(word => word.originalLanguage)
    );

    if (languages.length === 0) {
      if (IS_DEBUGGING) {
        console.warn(`cannot identify language of country ${country}`);
      }
    }

    if (languages.length > 1) {
      if (IS_DEBUGGING) {
        console.warn(`country ${country} has more than 1 language`);
      }
    }

    return languages;
  }

  getRandomLanguageFromCountry(country: string): string | null {
    const languages = this.getLanguagesFromCountry(country);
    if (languages.length === 0) {
      return null;
    }

    const index = Math.floor(Math.random() * languages.length);

    return languages[index];
  }
}

export interface WordFilter {
  originalLanguage?: string;
  originalWord?: string;
}

export class NullWordFilter implements WordFilter {}



// WEBPACK FOOTER //
// ./stores/word/LoanWordsStore.ts